/**
 * Utility functions shared across the application
 */

/**
 * Debounce function that delays the execution of a function until after a specified wait time
 * @param {Function} fn - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} The debounced function
 */
export function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

const SMOOTH_SCROLL_DURATION = 800;
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function easeInOutCubic(progress) {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

function getHeaderOffset() {
  const headerHeight = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--header-height')
  );

  return Number.isFinite(headerHeight) && headerHeight > 0 ? headerHeight : 0;
}

function getTargetScrollTop(element) {
  return element.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
}

function animateScrollTo(targetY, duration = SMOOTH_SCROLL_DURATION) {
  const startY = window.scrollY;
  const distance = targetY - startY;
  const startTime = performance.now();

  function step(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);

    window.scrollTo(0, startY + distance * easedProgress);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

function scrollToElement(element) {
  const targetY = getTargetScrollTop(element);

  if (reducedMotionQuery.matches) {
    window.scrollTo(0, targetY);
    return;
  }

  if ('scrollBehavior' in document.documentElement.style) {
    window.scrollTo({ top: targetY, left: 0, behavior: 'smooth' });
    return;
  }

  animateScrollTo(targetY);
}

function getHashTarget(hash) {
  if (!hash || hash === '#') return null;

  try {
    return document.querySelector(hash);
  } catch {
    return null;
  }
}

function handleAnchorClick(event) {
  const link = event.target.closest('a[href^="#"]');
  if (!link) return;
  if (link.hasAttribute('download')) return;
  if (link.closest('[data-no-smooth-scroll]')) return;

  const hash = link.getAttribute('href');
  const target = getHashTarget(hash);
  if (!target) return;

  event.preventDefault();
  scrollToElement(target);

  if (hash !== window.location.hash) {
    history.pushState(null, '', hash);
  }
}

function scrollToInitialHash() {
  const target = getHashTarget(window.location.hash);
  if (!target) return;

  requestAnimationFrame(() => {
    scrollToElement(target);
  });
}

function initSmoothScroll() {
  document.addEventListener('click', handleAnchorClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scrollToInitialHash, { once: true });
  } else {
    scrollToInitialHash();
  }
}

initSmoothScroll();
